import React, { useState, useEffect, useCallback } from "react";
import "./NotificationSystem.css";

/* ─────────────────────────────────────────────
   TOAST NOTIFICATION (top-right stack)
───────────────────────────────────────────── */
export const Toast = ({
  id,
  type = "info",
  title,
  message,
  duration = 4500,
  onClose,
}) => {
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    // Mount → slide in
    const showTimer = setTimeout(() => setVisible(true), 10);

    // Progress bar countdown
    const step = 100 / (duration / 50);
    const progressTimer = setInterval(() => {
      setProgress((p) => {
        if (p <= 0) {
          clearInterval(progressTimer);
          return 0;
        }
        return p - step;
      });
    }, 50);

    // Auto-dismiss
    const dismissTimer = setTimeout(() => handleClose(), duration);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(dismissTimer);
      clearInterval(progressTimer);
    };
  }, []);

  const handleClose = useCallback(() => {
    setVisible(false);
    setTimeout(() => onClose(id), 380); // wait for slide-out
  }, [id, onClose]);

  const config = {
    success: {
      icon: "fa-circle-check",
      label: "Success",
      accent: "#10b981",
      bg: "linear-gradient(135deg, #ecfdf5, #f0fdf4)",
      border: "rgba(16,185,129,0.22)",
      progress: "linear-gradient(90deg, #10b981, #059669)",
    },
    error: {
      icon: "fa-circle-xmark",
      label: "Error",
      accent: "#ef4444",
      bg: "linear-gradient(135deg, #fef2f2, #fff5f5)",
      border: "rgba(239,68,68,0.22)",
      progress: "linear-gradient(90deg, #ef4444, #dc2626)",
    },
    warning: {
      icon: "fa-triangle-exclamation",
      label: "Warning",
      accent: "#f59e0b",
      bg: "linear-gradient(135deg, #fffbeb, #fefce8)",
      border: "rgba(245,158,11,0.22)",
      progress: "linear-gradient(90deg, #f59e0b, #d97706)",
    },
    info: {
      icon: "fa-circle-info",
      label: "Info",
      accent: "#2563eb",
      bg: "linear-gradient(135deg, #eff6ff, #f0f9ff)",
      border: "rgba(37,99,235,0.18)",
      progress: "linear-gradient(90deg, #2563eb, #1d4ed8)",
    },
  };

  const c = config[type] || config.info;

  return (
    <div
      className={`wc-toast ${visible ? "wc-toast-visible" : ""}`}
      style={{ background: c.bg, borderColor: c.border }}
      role="alert"
    >
      {/* Left accent bar */}
      <div className="wc-toast-accent" style={{ background: c.accent }} />

      {/* Icon */}
      <div className="wc-toast-icon" style={{ color: c.accent }}>
        <i className={`fas ${c.icon}`} />
      </div>

      {/* Text */}
      <div className="wc-toast-body">
        <p className="wc-toast-title" style={{ color: c.accent }}>
          {title || c.label}
        </p>
        {message && <p className="wc-toast-message">{message}</p>}
      </div>

      {/* Close */}
      <button
        className="wc-toast-close"
        onClick={handleClose}
        style={{ color: c.accent }}
      >
        <i className="fas fa-xmark" />
      </button>

      {/* Progress bar */}
      <div className="wc-toast-progress-track">
        <div
          className="wc-toast-progress-fill"
          style={{ width: `${progress}%`, background: c.progress }}
        />
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   TOAST CONTAINER
───────────────────────────────────────────── */
export const ToastContainer = ({ toasts, removeToast }) => (
  <div className="wc-toast-container" aria-live="polite">
    {toasts.map((t) => (
      <Toast key={t.id} {...t} onClose={removeToast} />
    ))}
  </div>
);

/* ─────────────────────────────────────────────
   SUCCESS MODAL  (full booking confirmation)
───────────────────────────────────────────── */
export const SuccessModal = ({ isOpen, data, onClose, onGoHome }) => {
  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="wc-modal-backdrop"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="wc-modal">
        {/* Animated success ring */}
        <div className="wc-modal-icon-wrap">
          <div className="wc-modal-ring wc-ring-outer" />
          <div className="wc-modal-ring wc-ring-inner" />
          <div className="wc-modal-icon-circle">
            <i className="fas fa-check wc-modal-check-icon" />
          </div>
        </div>

        <h2 className="wc-modal-title">Consultation Booked!</h2>
        <p className="wc-modal-subtitle">
          Your free consultation has been successfully scheduled. Our expert
          advisor will reach out shortly.
        </p>

        {/* Booking details card */}
        {data && (
          <div className="wc-modal-details">
            {data.name && (
              <div className="wc-modal-detail-row">
                <div className="wc-modal-detail-icon">
                  <i className="fas fa-user" />
                </div>
                <div>
                  <span className="wc-modal-detail-label">Name</span>
                  <span className="wc-modal-detail-val">{data.name}</span>
                </div>
              </div>
            )}
            {data.email && (
              <div className="wc-modal-detail-row">
                <div className="wc-modal-detail-icon">
                  <i className="fas fa-envelope" />
                </div>
                <div>
                  <span className="wc-modal-detail-label">Confirmation to</span>
                  <span className="wc-modal-detail-val">{data.email}</span>
                </div>
              </div>
            )}
            {data.date && (
              <div className="wc-modal-detail-row">
                <div className="wc-modal-detail-icon">
                  <i className="fas fa-calendar-check" />
                </div>
                <div>
                  <span className="wc-modal-detail-label">Date</span>
                  <span className="wc-modal-detail-val">{data.date}</span>
                </div>
              </div>
            )}
            {data.time && (
              <div className="wc-modal-detail-row">
                <div className="wc-modal-detail-icon">
                  <i className="fas fa-clock" />
                </div>
                <div>
                  <span className="wc-modal-detail-label">Time</span>
                  <span className="wc-modal-detail-val">{data.time}</span>
                </div>
              </div>
            )}
          </div>
        )}

        <p className="wc-modal-note">
          <i className="fas fa-bell" /> You'll receive a confirmation email
          within a few minutes.
        </p>

        <div className="wc-modal-actions">
          <button
            className="wc-modal-btn wc-modal-btn-outline"
            onClick={onClose}
          >
            Stay Here
          </button>
          <button
            className="wc-modal-btn wc-modal-btn-primary"
            onClick={onGoHome}
          >
            Back to Home <i className="fas fa-arrow-right" />
          </button>
        </div>

        <button className="wc-modal-x" onClick={onClose}>
          <i className="fas fa-xmark" />
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   VALIDATION ERROR MODAL  (inline fields missing)
───────────────────────────────────────────── */
export const ErrorModal = ({ isOpen, title, message, onClose }) => {
  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="wc-modal-backdrop"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="wc-modal wc-modal-error">
        <div className="wc-modal-icon-wrap">
          <div className="wc-modal-ring wc-ring-outer wc-ring-error" />
          <div className="wc-modal-ring wc-ring-inner wc-ring-error" />
          <div className="wc-modal-icon-circle wc-circle-error">
            <i className="fas fa-exclamation wc-modal-check-icon" />
          </div>
        </div>

        <h2 className="wc-modal-title wc-title-error">{title || "Oops!"}</h2>
        <p className="wc-modal-subtitle">{message}</p>

        <div className="wc-modal-actions">
          <button className="wc-modal-btn wc-modal-btn-error" onClick={onClose}>
            Okay <i className="fas fa-pen" />
          </button>
        </div>

        <button className="wc-modal-x" onClick={onClose}>
          <i className="fas fa-xmark" />
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   useNotification HOOK
   Usage:
     const { toasts, removeToast, notify, showSuccess, showError } = useNotification();
───────────────────────────────────────────── */

export const useNotification = () => {
  const [toasts, setToasts] = useState([]);

  const notify = useCallback(({ type = "info", title, message, duration }) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, type, title, message, duration }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showSuccess = (title, message, duration) =>
    notify({ type: "success", title, message, duration });

  const showError = (title, message, duration) =>
    notify({ type: "error", title, message, duration });

  const showWarning = (title, message, duration) =>
    notify({ type: "warning", title, message, duration });

  const showInfo = (title, message, duration) =>
    notify({ type: "info", title, message, duration });

  return {
    toasts,
    removeToast,
    notify,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
};

export default {
  Toast,
  ToastContainer,
  SuccessModal,
  ErrorModal,
  useNotification,
};
